import { Box, Button, ButtonText, CheckCircleIcon, HStack, Icon, Pressable, Text, VStack, XCircleIcon } from '@gluestack-ui/themed';
import { useContext, useState } from 'react';
import { TranslationContext } from '../../../contexts/translations/Translation';
import { COLORS } from '../../../theme/colors';
import { StrainDTO } from '../../../generated/client-api-client';
import { pascalToSnake } from '../../../utils/StringUtils';

type TestValue = 'p' | 'm' | 'α' | 'β' | 'γ' | null;

const HEMOLYSIS_VALUES = ['α', 'β', 'γ'] as const;
const BINARY_VALUES = ['p', 'm'] as const;

const isHemolysisTest = (expected: string | undefined) =>
  expected && HEMOLYSIS_VALUES.includes(expected as typeof HEMOLYSIS_VALUES[number]);

interface IBiochemicalVerificationProps {
  strain: StrainDTO;
  neededTests: string[];
  topStrainConfidence?: string;
  onConfirm: (testValues: Record<string, TestValue>) => Promise<void>;
  onReportDiscordance: (testValues: Record<string, TestValue>) => void;
  onBack: () => void;
  isLoading?: boolean;
  testID?: string;
}

export const BiochemicalVerification = ({
  strain,
  neededTests,
  topStrainConfidence,
  onConfirm,
  onReportDiscordance,
  onBack,
  isLoading,
  testID,
}: IBiochemicalVerificationProps) => {
  const { i18n } = useContext(TranslationContext);
  const [testValues, setTestValues] = useState<Record<string, TestValue>>({});

  const getExpected = (key: string): string | undefined => {
    const raw = (strain as any)?.[key];
    if (!raw) return undefined;
    return raw.toString();
  };

  const isConcordant = (key: string): boolean | null => {
    const val = testValues[key];
    if (val === null || val === undefined) return null;
    const expected = getExpected(key);
    if (!expected) return null;
    return val === expected;
  };

  const allFilled = neededTests.every((k) => testValues[k] !== undefined && testValues[k] !== null);
  const anyDiscordant = neededTests.some((k) => isConcordant(k) === false);
  const allConcordant = allFilled && !anyDiscordant;

  return (
    <VStack gap={14}>
      <VStack gap={2}>
        <Text fontWeight="bold" fontSize={16}>
          {i18n?.t('page.analysis-details.verification.title')}
        </Text>
        <Text fontSize={13} color={COLORS.STATUS.SUCCESS}>
          {i18n?.t('page.analysis-details.verification.subtitle')}
        </Text>
      </VStack>

      <Box
        style={{
          backgroundColor: COLORS.FOUNDATION_BACKGROUND_70,
          borderRadius: 8,
          padding: 12,
        }}
      >
        <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
          {i18n?.t('page.analysis-details.verification.ai-prediction-label')}
        </Text>
        <HStack alignItems="center" gap={8} marginTop={2}>
          <Text fontWeight="bold" fontSize={14}>{strain.commonName}</Text>
          {topStrainConfidence && (
            <Box style={{ backgroundColor: COLORS.STATUS.SUCCESS, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 }}>
              <Text fontSize={11} color={COLORS.NEUTRAL[0]} fontWeight="bold">{topStrainConfidence}%</Text>
            </Box>
          )}
        </HStack>
      </Box>

      {neededTests.map((key) => {
        const expected = getExpected(key);
        const currentVal = testValues[key] ?? null;
        const concordant = isConcordant(key);
        const options = isHemolysisTest(expected) ? [...BINARY_VALUES, ...HEMOLYSIS_VALUES] : BINARY_VALUES;

        return (
          <Box
            key={key}
            testID={`${testID ?? ''}test_${key}`}
            style={{ borderBottomWidth: 1, borderBottomColor: COLORS.NEUTRAL[70], paddingBottom: 12 }}
          >
            <HStack justifyContent="space-between" alignItems="center" marginBottom={8}>
              <HStack alignItems="center" gap={6}>
                <Text fontSize={13} fontWeight="bold">
                  {i18n?.t(`properties.${pascalToSnake(key)}`)}
                </Text>
                {expected && (
                  <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
                    {i18n?.t('page.analysis-details.verification.expected-label')}{' '}
                    <Text fontSize={12} fontWeight="bold">
                      {i18n?.t(`page.pathogen-details.plusMinusNA.${expected}`, { defaultValue: expected })}
                    </Text>
                  </Text>
                )}
              </HStack>
              {concordant === true && (
                <Text fontSize={12} color={COLORS.STATUS.SUCCESS} fontWeight="bold">
                  {i18n?.t('page.analysis-details.verification.concordant')}
                </Text>
              )}
              {concordant === false && (
                <Text fontSize={12} color={COLORS.ACCENT[100]} fontWeight="bold">
                  {i18n?.t('page.analysis-details.verification.discordant')}
                </Text>
              )}
            </HStack>

            <HStack gap={8}>
              {options.map((opt) => {
                const isSelected = currentVal === opt;
                const selectedExpected = isSelected && expected && opt === expected;
                const selectedDiscordant = isSelected && expected && opt !== expected;
                return (
                  <Pressable
                    key={opt}
                    onPress={() => setTestValues((prev) => ({ ...prev, [key]: opt as TestValue }))}
                    style={{
                      borderWidth: 1,
                      borderColor: selectedExpected
                        ? COLORS.STATUS.SUCCESS
                        : selectedDiscordant
                        ? COLORS.ACCENT[100]
                        : isSelected
                        ? COLORS.ACCENT[100]
                        : COLORS.NEUTRAL[70],
                      borderRadius: 6,
                      paddingHorizontal: 14,
                      paddingVertical: 6,
                      backgroundColor: isSelected ? 'transparent' : 'transparent',
                    }}
                  >
                    <Text
                      fontSize={13}
                      fontWeight={isSelected ? 'bold' : 'normal'}
                      color={
                        selectedExpected
                          ? COLORS.STATUS.SUCCESS
                          : selectedDiscordant
                          ? COLORS.ACCENT[100]
                          : isSelected
                          ? COLORS.ACCENT[100]
                          : COLORS.NEUTRAL[90]
                      }
                    >
                      {opt === 'p' ? '+' : opt === 'm' ? '-' : opt}
                    </Text>
                  </Pressable>
                );
              })}
            </HStack>
          </Box>
        );
      })}

      <Box style={{ minHeight: 110 }}>
        {allFilled && allConcordant && (
          <Box style={{ backgroundColor: '#F0FBF5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#A8DFC0' }}>
            <HStack alignItems="center" gap={6} marginBottom={4}>
              <Icon as={CheckCircleIcon} color={COLORS.STATUS.SUCCESS} size="sm" />
              <Text fontWeight="bold" color={COLORS.STATUS.SUCCESS}>
                {i18n?.t('page.analysis-details.verification.confirmed-banner')}
              </Text>
            </HStack>
            <Text fontSize={12} color={COLORS.FOUNDATION_TERCIARY_100}>
              {i18n?.t('page.analysis-details.verification.confirmed-message', { strain: strain.commonName })}
            </Text>
          </Box>
        )}

        {allFilled && anyDiscordant && (
          <Box style={{ backgroundColor: '#FFF5F4', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#F5C6C0' }}>
            <HStack alignItems="center" gap={6} marginBottom={4}>
              <Icon as={XCircleIcon} color={COLORS.ACCENT[100]} size="sm" />
              <Text fontWeight="bold" color={COLORS.ACCENT[100]}>
                {i18n?.t('page.analysis-details.verification.discordance-banner')}
              </Text>
            </HStack>
            <Text fontSize={12} color={COLORS.FOUNDATION_TERCIARY_100}>
              {i18n?.t('page.analysis-details.verification.discordance-message', { strain: strain.commonName })}
            </Text>
          </Box>
        )}

        {!allFilled && (
          <Text fontSize={12} color={COLORS.NEUTRAL[90]} fontStyle="italic">
            {i18n?.t('page.analysis-details.verification.fill-all-tests')}
          </Text>
        )}
      </Box>

      <VStack gap={8} marginTop={4}>
        {allConcordant && (
          <Button
            bg={COLORS.STATUS.SUCCESS}
            $hover-bg="#05A860"
            borderRadius={10}
            width="100%"
            onPress={() => onConfirm(testValues)}
            isDisabled={isLoading}
          >
            <ButtonText style={{ color: COLORS.NEUTRAL[0] }}>
              {i18n?.t('page.analysis-details.verification.confirm-button')}
            </ButtonText>
          </Button>
        )}
        {anyDiscordant && (
          <Button
            bg={COLORS.ACCENT[100]}
            $hover-bg={COLORS.ACCENT[110]}
            borderRadius={10}
            width="100%"
            onPress={() => onReportDiscordance(testValues)}
            isDisabled={isLoading}
          >
            <ButtonText style={{ color: COLORS.NEUTRAL[0] }}>
              {i18n?.t('page.analysis-details.verification.report-discordance-button')}
            </ButtonText>
          </Button>
        )}
        <Pressable onPress={() => onBack()} alignSelf="center" marginTop={4}>
          <Text fontSize={13} color={COLORS.ACCENT[100]}>
            {i18n?.t('page.analysis-details.verification.back-to-strain')}
          </Text>
        </Pressable>
      </VStack>
    </VStack>
  );
};
